// Viewer 2.0 Zustand store (framework-agnostic state; no MapLibre import).
//
// Holds, per side, a DRAFT config (what the config bar edits) and an APPLIED
// config (what the map currently renders). The Update action promotes draft →
// applied; `isDirty` reflects when they differ so the Update button can signal
// pending changes. Instant toggles and camera live here too. See
// docs/viewer-v2/PLAN.md §3 ("draft-vs-applied").

import { create } from "zustand";
import {
    DEFAULT_CAMERA,
    DEFAULT_TOGGLES,
    InspectMode,
    MapSide,
    SideConfig,
    ViewerCamera,
    ViewerToggles,
    defaultSideConfig,
} from "./viewerTypes";

/** Deep-ish equality for a side config (flat object of primitives). */
function sideConfigEquals(a: SideConfig, b: SideConfig): boolean {
    return (
        a.selectionType === b.selectionType &&
        a.environment === b.environment &&
        a.stylesheetId === b.stylesheetId &&
        a.stylesheetVersion === b.stylesheetVersion &&
        a.manualStylesheetUrl === b.manualStylesheetUrl &&
        a.tilesetUrl === b.tilesetUrl &&
        a.label === b.label
    );
}

export interface ViewerStoreState {
    /** Whether this viewer instance has a right side (compare/slider modes). */
    hasRight: boolean;

    /** Draft (editable) configs. `right` is meaningful only when hasRight. */
    draft: { left: SideConfig; right: SideConfig };
    /** Applied configs — what the map renders. null until first Update. */
    applied: { left: SideConfig | null; right: SideConfig | null };

    /** Instant client-side toggles. */
    toggles: ViewerToggles;
    /** Current camera. */
    camera: ViewerCamera;

    // -- Draft editing (no fetch) --
    /** Patches one side's draft config. */
    setDraft: (side: MapSide, patch: Partial<SideConfig>) => void;
    /** Sets a side's free-text label (draft). */
    setLabel: (side: MapSide, label: string) => void;

    // -- Apply (Update button) --
    /** Promotes a side's draft to applied (call after a successful fetch). */
    applySide: (side: MapSide) => void;
    /** Promotes all relevant sides' drafts to applied. */
    applyAll: () => void;

    // -- Instant toggles --
    setXray: (on: boolean) => void;
    setTileBoundaries: (on: boolean) => void;
    setInspect: (mode: InspectMode) => void;

    // -- Camera --
    setCamera: (camera: ViewerCamera) => void;

    // -- Derived --
    /** True when a side's draft differs from its applied config (needs Update). */
    isDirty: (side: MapSide) => boolean;
}

/** Creates the initial store state for a given mode. Exported for testing. */
export function createInitialState(hasRight: boolean) {
    return {
        hasRight,
        draft: { left: defaultSideConfig("Left"), right: defaultSideConfig("Right") },
        applied: { left: null as SideConfig | null, right: null as SideConfig | null },
        toggles: { ...DEFAULT_TOGGLES },
        camera: { ...DEFAULT_CAMERA },
    };
}

export const useViewerStore = create<ViewerStoreState>((set, get) => ({
    ...createInitialState(false),

    setDraft: (side, patch) =>
        set((state) => ({
            draft: { ...state.draft, [side]: { ...state.draft[side], ...patch } },
        })),

    setLabel: (side, label) =>
        set((state) => ({
            draft: { ...state.draft, [side]: { ...state.draft[side], label } },
        })),

    applySide: (side) =>
        set((state) => ({
            applied: { ...state.applied, [side]: { ...state.draft[side] } },
        })),

    applyAll: () =>
        set((state) => ({
            applied: {
                left: { ...state.draft.left },
                right: state.hasRight ? { ...state.draft.right } : state.applied.right,
            },
        })),

    setXray: (on) => set((state) => ({ toggles: { ...state.toggles, xray: on } })),
    setTileBoundaries: (on) =>
        set((state) => ({ toggles: { ...state.toggles, tileBoundaries: on } })),
    setInspect: (mode) => set((state) => ({ toggles: { ...state.toggles, inspect: mode } })),

    setCamera: (camera) => set({ camera }),

    isDirty: (side) => {
        const { draft, applied } = get();
        // Never applied yet → dirty (there are pending changes to render).
        if (!applied[side]) {
            return true;
        }
        return !sideConfigEquals(draft[side], applied[side] as SideConfig);
    },
}));
